import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils/classNames';

/**
 * GGC V2. Callout Box — Figma node 166:522 family (Component 03).
 *
 * Inline informational/warning callout with a strong 2px border and a
 * title + body block. Larger and more prominent than `GgcAlert`; use it
 * for important guidance in forms and detail pages.
 *
 * Spec: r-md, padding 10x14, border-2. Title 12px semi-bold + body 11px
 * regular. Two tones: warning (orange) and info (blue).
 */
const ggcCalloutVariants = cva(
  'flex flex-col gap-[3px] rounded-ggc-md px-[14px] py-[10px] font-ggc',
  {
    variants: {
      tone: {
        warning: 'bg-ggc-orange-light border-ggc-orange text-ggc-orange',
        info: 'bg-ggc-blue-light border-ggc-blue text-ggc-blue',
      },
    },
    defaultVariants: {
      tone: 'info',
    },
  }
);

const iconGlyphMap: Record<NonNullable<GgcCalloutProps['tone']>, string> = {
  warning: '⚠',
  info: 'ⓘ',
};

export interface GgcCalloutProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'>,
    VariantProps<typeof ggcCalloutVariants> {
  /** Bold title row, e.g. "Budget Alert". */
  title: React.ReactNode;
  /** Icon glyph override; falls back to tone default. */
  icon?: React.ReactNode;
}

const GgcCallout = React.forwardRef<HTMLDivElement, GgcCalloutProps>(
  ({ className, tone, title, icon, children, ...props }, ref) => {
    const resolvedTone = tone ?? 'info';
    return (
      <div ref={ref} className={cn(ggcCalloutVariants({ tone: resolvedTone }), className)} {...props}>
        <p className="text-[12px] font-semibold leading-[1.5]">
          <span aria-hidden className="mr-1">
            {icon ?? iconGlyphMap[resolvedTone]}
          </span>
          {title}
        </p>
        {children && <p className="text-[11px] leading-[1.5]">{children}</p>}
      </div>
    );
  }
);
GgcCallout.displayName = 'GgcCallout';

export { GgcCallout, ggcCalloutVariants };
